import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Link } from 'react-router-dom';

const CmmiConsultancy: React.FC = () => {
  const consultancyServices = [
    {
      title: "Gap Analysis",
      description: "We identify gaps between your current processes and CMMI best practices to develop a tailored improvement plan.",
      icon: <AssessmentIcon fontSize="large" />,
      benefits: [
        "Understand your organization's current process maturity",
        "Identify specific areas for improvement",
        "Prioritize improvement efforts",
        "Set realistic targets and timelines"
      ]
    },
    {
      title: "Process Development",
      description: "We help you develop and document processes that align with CMMI best practices and your business objectives.",
      icon: <SettingsIcon fontSize="large" />,
      benefits: [
        "Standardized processes across the organization",
        "Improved efficiency and productivity",
        "Reduced operational risks",
        "Better quality products and services"
      ]
    },
    {
      title: "CMMI Training",
      description: "We provide comprehensive training on CMMI concepts, practices, and implementation strategies.",
      icon: <SchoolIcon fontSize="large" />,
      benefits: [
        "Build internal capability and knowledge",
        "Empower your team to drive process improvement",
        "Ensure consistent understanding of CMMI principles",
        "Prepare your team for appraisals"
      ]
    },
    {
      title: "Appraisal Preparation",
      description: "We help you prepare for formal CMMI appraisals to ensure successful certification.",
      icon: <BuildIcon fontSize="large" />,
      benefits: [
        "Pre-appraisal assessments to identify potential issues",
        "Mock appraisals to simulate the real experience",
        "Evidence collection guidance",
        "Strategic advice to optimize appraisal outcomes"
      ]
    }
  ];

  const faqs = [
    {
      question: "How long does CMMI implementation typically take?",
      answer: "The implementation timeframe depends on various factors, including your organization's size, current process maturity, and target capability level. Typically, a CMMI Level 2 implementation might take 6-12 months, while achieving Level 3 might take 12-18 months. During our initial assessment, we'll provide a more accurate timeline based on your specific needs."
    },
    {
      question: "Can you help us prepare for a specific CMMI level?",
      answer: "Yes, we can help you prepare for any CMMI capability level (1-5). Our consultants will work with you to determine the most appropriate level for your organization based on your business goals, current process maturity, and available resources."
    },
    {
      question: "How do you ensure CMMI implementation aligns with our business goals?",
      answer: "Our approach is business-driven rather than compliance-driven. We start by understanding your business objectives and challenges, then customize CMMI implementation to address your specific needs while maintaining alignment with the CMMI model requirements. This ensures that process improvements drive real business value."
    },
    {
      question: "What resources will we need to commit to CMMI implementation?",
      answer: "Successful CMMI implementation requires commitment from executive leadership, allocation of staff resources (especially process owners and improvement teams), and some investment in tools and infrastructure. We'll help you determine the appropriate resource allocation based on your specific implementation plan."
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              CMMI Consultancy Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Expert guidance to implement CMMI best practices and improve your organizational processes
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Our CMMI Consultancy Approach
              </Typography>
              <Typography paragraph>
                At Right Track Technologies Limited, our CMMI consultancy services are designed to help organizations
                implement effective process improvement initiatives and achieve CMMI maturity levels that align with
                their business objectives. We provide expert guidance throughout your CMMI journey.
              </Typography>
              <Typography paragraph>
                We understand that every organization is unique, which is why we take a customized approach 
                to CMMI implementation. Whether you're looking to achieve a specific CMMI level or simply 
                improve your organizational processes, our consultants will develop a tailored plan that 
                delivers measurable results.
              </Typography>
              <Typography paragraph>
                Our goal is not just to help you achieve CMMI certification, but to implement sustainable 
                process improvements that drive business value and competitive advantage.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMI Consultancy"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Consultancy Services
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Our comprehensive CMMI consultancy services are designed to help your organization
              implement effective process improvements and achieve CMMI certification.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 3 
          }}>
            {consultancyServices.map((service, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            backgroundColor: '#1976d215',
                            color: '#1976d2',
                            mr: 3
                          }}
                        >
                          {service.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          fontWeight="bold"
                        >
                          {service.title}
                        </Typography>
                      </Box>
                      
                      <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                        {service.description}
                      </Typography>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      <Typography variant="h6" gutterBottom fontWeight="medium">
                        Benefits:
                      </Typography>
                      
                      <List>
                        {service.benefits.map((benefit, idx) => (
                          <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={benefit} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMI Consultancy Team"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Why Choose Our CMMI Consultancy?
              </Typography>
              <Typography paragraph sx={{ mb: 4 }}>
                Right Track Technologies Limited offers industry-leading CMMI consultancy services with a focus on
                practical implementation, sustainable process improvement, and measurable results. Our team of
                experienced consultants brings expertise across multiple domains and industries.
              </Typography>
              
              <List>
                {[
                  "Experienced consultants certified in CMMI",
                  "Proven track record of successful CMMI implementations",
                  "Practical, business-focused approach",
                  "Customized solutions tailored to your specific needs",
                  "Comprehensive support from initial assessment to certification",
                  "Knowledge transfer and capability building",
                  "Ongoing support and sustainment services"
                ].map((item, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* FAQs Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Get answers to common questions about our CMMI consultancy services.
            </Typography>
          </motion.div>
          
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Accordion
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    <Typography fontWeight="medium">{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, py: 2, bgcolor: 'rgba(245, 247, 250, 0.5)' }}>
                    <Typography color="text.secondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Improve Your Processes?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our CMMI consultants are ready to help you implement effective process improvements 
                and achieve CMMI certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=cmmi-consultancy"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/cmmi"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  Back to CMMI Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CmmiConsultancy; 